<template>
    <div
        :class="classObjectRoot"
        data-spec-class="alert"
    >
        <div class="flex">
            <div class="shrink-0">
                <component
                    :is="getIcon()"
                    :class="classObjectIcon"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-2">
                <h3
                    :class="classObjectMessage"
                    data-spec-class="alert-message"
                >
                    <slot />
                </h3>
                <div
                    v-if="!!$slots.description"
                    :class="classObjectDescription"
                    data-spec-class="alert-description"
                >
                    <slot name="description" />
                </div>
                <div
                    v-if="!!$slots.actions"
                    class="mt-4"
                >
                    <div class="-mx-2 -my-1.5 flex">
                        <slot name="actions" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, defineProps, withDefaults } from 'vue';
import {
    CheckCircleIcon as CheckCircleIconSolid,
    InformationCircleIcon as InformationCircleIconSolid,
    ExclamationTriangleIcon as ExclamationTriangleIconSolid,
    XCircleIcon as XCircleIconSolid,
} from '@heroicons/vue/24/solid';

import {
    CheckCircleIcon as CheckCircleIconOutline,
    InformationCircleIcon as InformationCircleIconOutline,
    ExclamationTriangleIcon as ExclamationTriangleIconOutline,
    XCircleIcon as XCircleIconOutline,
} from '@heroicons/vue/24/outline';
import { Functional, SizeAll } from '@/types';

interface Props {
    type?: Functional;
    size?: SizeAll;
    theme?: string;
}

const props = withDefaults(defineProps<Props>(), {
    type: Functional.Warning,
    size: SizeAll.Normal,
    theme: 'solid',
});

const classObjectRoot = computed(() => ({
    'rounded-md': true,

    'p-2': props.size === SizeAll.Tiny,
    'p-4': props.size === SizeAll.Small,
    'px-10 py-7': props.size === SizeAll.Normal,

    'border !border-red': props.type === Functional.Error && props.theme === 'solid',
    'border !border-orange-400': props.type === Functional.Warning && props.theme === 'solid',
    'border !border-blue-400': props.type === Functional.Info && props.theme === 'solid',
    'border !border-green': props.type === Functional.Success && props.theme === 'solid',

    // outline theme only used in fe. Can be removed when we migrate to
    // the new design system. #WEB-1253
    'border border-red': props.type === Functional.Error && props.theme === 'outline',
    'border border-orange-700': props.type === Functional.Warning && props.theme === 'outline',
    'border border-blue-700': props.type === Functional.Info && props.theme === 'outline',
    'border border-green': props.type === Functional.Success && props.theme === 'outline',
}));

const classObjectIcon = computed(() => ({
    'mt-[1px]': true,
    'h-4 w-4': props.size === SizeAll.Tiny,
    'h-6 w-6': props.size === SizeAll.Small,
    'h-8 w-8': props.size === SizeAll.Normal,
    'text-red': props.type === Functional.Error,
    'text-orange-700': props.type === Functional.Warning,
    'text-blue-700': props.type === Functional.Info,
    'text-green': props.type === Functional.Success,
}));

const classObjectMessage = computed(() => ({
    'text-xs': props.size === SizeAll.Tiny,
    'text-medium': props.size === SizeAll.Small,
    'text-xl': props.size === SizeAll.Normal,
    'text-blue-700': Functional.Info === props.type && props.theme === 'outline',
    'text-green': Functional.Success === props.type && props.theme === 'outline',
    'text-red': Functional.Error === props.type && props.theme === 'outline',
    'text-orange-700': Functional.Warning === props.type && props.theme === 'outline',
}));

const classObjectDescription = computed(() => ({
    'text-tertiary': true,
    'mt-1 text-xs': props.size === SizeAll.Small,
    'mt-4': props.size === SizeAll.Normal,
}));

function getIcon() {
    switch (props.type) {
        case Functional.Error:
            if (props.theme === 'outline') {
                return XCircleIconOutline;
            }
            return XCircleIconSolid;
        case Functional.Warning:
            if (props.theme === 'outline') {
                return ExclamationTriangleIconOutline;
            }
            return ExclamationTriangleIconSolid;
        case Functional.Info:
            if (props.theme === 'outline') {
                return InformationCircleIconOutline;
            }
            return InformationCircleIconSolid;
        case Functional.Success:
            if (props.theme === 'outline') {
                return CheckCircleIconOutline;
            }
            return CheckCircleIconSolid;
        default:
            if (props.theme === 'outline') {
                return InformationCircleIconOutline;
            }
            return InformationCircleIconSolid;
    }
}
</script>
